import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { smartMatchingService, QuickAssessmentData, QuickAssessmentResult, ComprehensiveMatches } from '../services/smart-matching.service'

export const useQuickAssessment = () => {
  return useMutation({
    mutationFn: (assessmentData: QuickAssessmentData) =>
      smartMatchingService.performQuickAssessment(assessmentData),
  })
}

export const useComprehensiveMatches = () => {
  return useMutation({
    mutationFn: (params: {
      location: string
      urgencyLevel: string
      preferredBurialArea: string
      specialRequirements?: string[]
      selectedPackage?: {
        id: string
        name: string
        price: number
        includes: any[]
        excludes: any[]
      }
    }) => smartMatchingService.findComprehensiveMatches(params),
  })
}

export const useAmbulanceMatches = () => {
  return useMutation({
    mutationFn: (params: {
      location: string
      urgencyLevel: string
      maxDistance?: number
      limit?: number
    }) => smartMatchingService.findAmbulanceMatches(params),
  })
}

export const useUstadzMatches = () => {
  return useMutation({
    mutationFn: (params: {
      location: string
      requiredServices?: string[]
      maxDistance?: number
      limit?: number
    }) => smartMatchingService.findUstadzMatches(params),
  })
}

export const useBurialGroundMatches = () => {
  return useMutation({
    mutationFn: (params: {
      preferredArea: string
      religiousRequirements?: string[]
      maxDistance?: number
      limit?: number
    }) => smartMatchingService.findBurialGroundMatches(params),
  })
}

export const useQuickAmbulanceMatch = () => {
  return useMutation({
    mutationFn: ({ location, urgency }: { location: string; urgency: string }) =>
      smartMatchingService.quickAmbulanceMatch(location, urgency),
  })
}

export const useQuickUstadzMatch = () => {
  return useMutation({
    mutationFn: ({ location, services }: { location: string; services?: string }) =>
      smartMatchingService.quickUstadzMatch(location, services),
  })
}

export const useQuickBurialMatch = () => {
  return useMutation({
    mutationFn: ({ area, requirements }: { area: string; requirements?: string }) =>
      smartMatchingService.quickBurialMatch(area, requirements),
  })
}

export const useCreateCaseWithProviders = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (caseData: {
      deceasedData: any
      assessmentData: any
      selectedPackageId?: string
      customPackageConfig?: any
      providerMatchingData?: any
      selectedProviders?: any[]
      providersSkipped?: boolean
    }) => smartMatchingService.createCaseWithProviders(caseData),
    onSuccess: () => {
      // Invalidate cases cache to refresh the cases list
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] })
    },
  })
}

export const useBookProviderForCase = () => {
  return useMutation({
    mutationFn: ({ caseId, bookingData }: {
      caseId: string;
      bookingData: {
        providerId: string;
        serviceType: string;
        scheduledDateTime: string;
        duration?: number;
        location?: string;
        coordinates?: { lat: number; lng: number };
        specialRequests?: string;
        pricing?: any;
      };
    }) => smartMatchingService.bookProviderForCase(caseId, bookingData),
  })
}

export const useCaseBookings = (caseId?: string) => {
  return useQuery({
    queryKey: ['case-bookings', caseId],
    queryFn: () => smartMatchingService.getCaseBookings(caseId!),
    enabled: !!caseId,
  })
}